import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useSectionTranslation, useDocumentTitle } from '@/hooks';
import { useLocale } from '@/lib/i18n';
import { api, isApiError } from '@/lib/api';
import { formatPrice } from '@/lib/formatPrice';
import { useAuth } from '@/lib/auth/AuthContext';
import { dashboardTranslations } from './translations';
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  History,
  Loader2,
  ShieldCheck,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  Printer,
  Download,
  Copy,
} from 'lucide-react';

// Backend getDepositStatus has a 15s BTCPay sync cooldown — polling faster
// only re-reads stored state, so stay at ~20s to avoid pointless calls.
const DEPOSIT_POLL_INTERVAL_MS = 20_000;
const TERMINAL_DEPOSIT_STATUSES = new Set(['COMPLETED', 'EXPIRED', 'FAILED']);
const DEPOSIT_MIN_AMOUNT = 0.01;
const DEPOSIT_MAX_AMOUNT = 1_000_000;

interface WalletBalance {
  currency: string;
  availableBalance: string;
  pendingBalance: string;
  escrowBalance: string;
}

interface WalletData {
  userId: string;
  balances: WalletBalance[];
}

interface WithdrawResult {
  id: string;
  status: string;
  amount: string;
  currency: string;
}

interface Transaction {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAWAL' | 'PAYOUT';
  amount: string;
  currency?: string;
  status: 'PENDING' | 'PROCESSING' | 'CONFIRMING' | 'CONFIRMED' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  createdAt: string;
}

interface DepositResult {
  txId: string;
  invoiceId: string;
  checkoutUrl: string;
  amount: string;
  currency: string;
  status: string;
}

interface DepositStatus {
  id: string;
  status: string;
  amount: string;
  currency: string;
}

export function WalletPage() {
  const t = useSectionTranslation(dashboardTranslations);
  const wp = t.walletPage;
  const { locale } = useLocale();
  const { user, loading: authLoading } = useAuth();
  useDocumentTitle(wp.title);

  const format = (amount: number | string, currency?: string) =>
    formatPrice(amount, currency || 'USDT', locale);
  const printAllTransactions = () => {
    const rows = transactions
      .map((tx) => `<tr><td>${tx.type}</td><td>${format(tx.amount, tx.currency)}</td><td>${tx.status}</td><td>${formatDate(tx.createdAt)}</td></tr>`)
      .join('');
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write(`
      <html><head><title>${wp.history.exportTitle}</title>
      <style>body{font-family:monospace;padding:24px;background:#0a0a0f;color:#f5f5f7}
      h1{font-size:18px;margin-bottom:16px;color:#f5c518}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th{text-align:left;padding:8px 10px;border-bottom:2px solid rgba(255,255,255,0.1);color:#8b8b96;text-transform:uppercase;font-size:11px}
      td{padding:8px 10px;border-bottom:1px solid rgba(255,255,255,0.06)}
      @media print{body{background:#fff;color:#000}h1{color:#000}th{color:#555;border-color:#ddd}td{border-color:#ddd}}
      </style></head><body>
      <h1>${wp.history.exportTitleDate.replace('{date}', new Date().toLocaleDateString(locale))}</h1>
      <table><thead><tr><th>${wp.history.type}</th><th>${wp.history.amount}</th><th>${wp.history.status}</th><th>${wp.history.date}</th></tr></thead><tbody>${rows}</tbody></table>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );
  const formatDate = (date: string) => dateFormatter.format(new Date(date));

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const handleCopy = (field: string, text: string) => {
    if (!navigator.clipboard) return;
    void navigator.clipboard.writeText(text).then(() => {
      if (!mountedRef.current) return;
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      setCopiedField(field);
      copyTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setCopiedField(null);
      }, 2000);
    });
  };

  const walletErrorText = (code: string | undefined, fallback: string) => {
    switch (code) {
      case 'NETWORK_ERROR':
        return wp.errors.network;
      case 'PAYMENTS_UNAVAILABLE':
        return wp.deposit.unavailable;
      case 'PAYOUT_UNAVAILABLE':
        return wp.withdraw.unavailable;
      case 'INSUFFICIENT_BALANCE':
        return wp.withdraw.insufficient;
      case 'VALIDATION_ERROR':
        return wp.errors.invalidInput;
      default:
        return fallback;
    }
  };

  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState<string | null>(null);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [txPage, setTxPage] = useState(0);
  const [txPageSize, setTxPageSize] = useState(10);

  const [depositAmount, setDepositAmount] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [depositResult, setDepositResult] = useState<DepositResult | null>(null);
  const [depositStatus, setDepositStatus] = useState<string | null>(null);
  const [depositPollError, setDepositPollError] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const [exportOpen, setExportOpen] = useState(false);

  const [withdrawCurrency, setWithdrawCurrency] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDestination, setWithdrawDestination] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawResult, setWithdrawResult] = useState<WithdrawResult | null>(null);
  const [withdrawKey, setWithdrawKey] = useState(() => crypto.randomUUID());

  const exportCSV = (txs: Transaction[]) => {
    const header = [
      wp.history.id,
      wp.history.type,
      wp.history.amount,
      wp.history.currency,
      wp.history.status,
      wp.history.date,
    ].join(',');
    const rows = txs.map((tx) =>
      [tx.id, tx.type, tx.amount, tx.currency || '', tx.status, new Date(tx.createdAt).toISOString()].join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printTransaction = (tx: Transaction) => {
    const win = window.open('', '_blank', 'width=700,height=500');
    if (!win) return;
    win.document.write(`
      <html><head><title>${wp.history.receiptTitleId.replace('{id}', tx.id.slice(0, 8))}</title>
      <style>body{font-family:monospace;padding:32px;background:#0a0a0f;color:#f5f5f7}
      h1{font-size:20px;margin-bottom:24px;color:#f5c518}
      table{width:100%;border-collapse:collapse}
      td{padding:10px 14px;border-bottom:1px solid rgba(255,255,255,0.06)}
      td:first-child{color:#8b8b96;width:120px}
      td:last-child{font-weight:600}
      @media print{body{background:#fff;color:#000}h1{color:#000}}
      </style></head><body>
      <h1>${wp.history.receiptTitle}</h1>
      <table>
        <tr><td>${wp.history.id}</td><td>${tx.id}</td></tr>
        <tr><td>${wp.history.type}</td><td>${tx.type}</td></tr>
        <tr><td>${wp.history.amount}</td><td>${format(tx.amount, tx.currency)}</td></tr>
        <tr><td>${wp.history.status}</td><td>${tx.status}</td></tr>
        <tr><td>${wp.history.date}</td><td>${formatDate(tx.createdAt)}</td></tr>
      </table></body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const fetchWallet = useCallback(async () => {
    setWalletLoading(true);
    setWalletError(null);
    const res = await api.get<WalletData>('/wallet');
    if (isApiError(res)) {
      setWalletError(res.error?.code === 'NETWORK_ERROR' ? wp.errors.network : wp.errors.generic);
    } else {
      setWallet(res.data);
    }
    setWalletLoading(false);
  }, [wp.errors.generic, wp.errors.network]);

  const fetchTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    setTransactionsError(null);
    const res = await api.get<Transaction[]>('/wallet/transactions');
    if (isApiError(res)) {
      setTransactionsError(res.error?.code === 'NETWORK_ERROR' ? wp.errors.network : wp.errors.generic);
    } else {
      setTransactions(res.data);
    }
    setTransactionsLoading(false);
  }, [wp.errors.generic, wp.errors.network]);

  useEffect(() => {
    if (!user) return;
    fetchWallet();
    fetchTransactions();
  }, [user, fetchWallet, fetchTransactions]);

  useEffect(() => {
    if (!withdrawCurrency && wallet && wallet.balances.length > 0) {
      setWithdrawCurrency(wallet.balances[0].currency);
    }
  }, [wallet, withdrawCurrency]);

  useEffect(() => {
    if (!depositResult) return;
    let cancelled = false;
    let pollSeq = 0;
    let intervalId: number | undefined;

    const pollStatus = async () => {
      const seq = ++pollSeq;
      const res = await api.get<DepositStatus>(`/wallet/deposit/${depositResult.txId}/status`);
      if (cancelled || seq !== pollSeq) return;
      if (isApiError(res)) {
        setDepositPollError(true);
        return;
      }
      setDepositPollError(false);
      const nextStatus = res.data.status;
      setDepositStatus(nextStatus);
      if (nextStatus === 'COMPLETED') {
        fetchWallet();
        fetchTransactions();
      }
      if (TERMINAL_DEPOSIT_STATUSES.has(nextStatus) && intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };

    void pollStatus();
    intervalId = window.setInterval(pollStatus, DEPOSIT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [depositResult, fetchWallet, fetchTransactions]);

  const handleDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (depositLoading) return;
    const amount = Number(depositAmount);
    if (!Number.isFinite(amount) || amount < DEPOSIT_MIN_AMOUNT || amount > DEPOSIT_MAX_AMOUNT) {
      setDepositError(wp.deposit.amountError);
      return;
    }
    setDepositError(null);
    setDepositResult(null);
    setDepositStatus(null);
    setDepositPollError(false);
    setDepositLoading(true);

    const res = await api.post<DepositResult>('/wallet/deposit', { amount, currency: 'USDT' });
    if (isApiError(res)) {
      setDepositError(walletErrorText(res.error?.code, wp.errors.generic));
    } else {
      setDepositResult(res.data);
      setDepositStatus(res.data.status);
      window.open(res.data.checkoutUrl, '_blank', 'noopener,noreferrer');
    }
    setDepositLoading(false);
  };

  const handleNewDeposit = () => {
    setDepositResult(null);
    setDepositStatus(null);
    setDepositPollError(false);
    setDepositError(null);
    setCopiedField(null);
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    const currency = withdrawCurrency || wallet?.balances[0]?.currency;
    if (!currency) return;
    const amount = Number(withdrawAmount);
    if (!Number.isFinite(amount) || amount < DEPOSIT_MIN_AMOUNT || amount > DEPOSIT_MAX_AMOUNT) {
      setWithdrawError(wp.withdraw.amountError);
      return;
    }
    setWithdrawError(null);
    setWithdrawResult(null);
    setWithdrawLoading(true);

    const res = await api.post<WithdrawResult>('/wallet/withdraw', {
      amount,
      currency,
      destination: withdrawDestination.trim() || undefined,
      idempotencyKey: withdrawKey,
    });
    if (isApiError(res)) {
      setWithdrawError(walletErrorText(res.error?.code, wp.errors.generic));
    } else {
      setWithdrawResult(res.data);
      setWithdrawAmount('');
      setWithdrawDestination('');
      setWithdrawKey(crypto.randomUUID());
      fetchWallet();
      fetchTransactions();
    }
    setWithdrawLoading(false);
  };

  const statusBadge = (status: Transaction['status']) => {
    const isGood = status === 'COMPLETED' || status === 'CONFIRMED';
    const isBad = status === 'FAILED' || status === 'EXPIRED';
    const cls = isGood
      ? 'badge-green'
      : isBad
        ? 'bg-brand-danger/10 text-brand-danger border border-brand-danger/10 rounded text-[11px] font-medium uppercase tracking-wide inline-flex items-center gap-1 px-2 py-0.5'
        : 'badge-accent';
    return <span className={`badge ${cls}`}>{wp.history.statuses[status]}</span>;
  };

  const depositStatusLabel = (status: string) => {
    const key = status as keyof typeof wp.history.statuses;
    return wp.history.statuses[key] ?? status;
  };

  const isInitialLoad = !wallet && transactions.length === 0;

  if (walletLoading && transactionsLoading && isInitialLoad) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-accent" />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={22} className="animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth/signin?from=${encodeURIComponent(window.location.pathname)}`} replace />;
  }

  return (
    <div className="space-y-8">
      <div className="mb-10 max-w-3xl">
        <div className="flex items-center gap-2 mb-2">
          <Wallet size={24} className="text-accent" />
          <h1 className="font-display text-display-md text-brand-white">{wp.heading}</h1>
        </div>
        <p className="text-body-md text-brand-muted">{wp.description}</p>
      </div>

      {walletLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={22} className="animate-spin text-accent" />
        </div>
      ) : walletError ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
          role="alert"
        >
          <AlertCircle size={16} />
          {walletError}
        </div>
      ) : wallet ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {wallet.balances.length === 0 ? (
            <div className="p-6 rounded-2xl bg-surface-card border border-surface-border sm:col-span-2 lg:col-span-4">
              <p className="text-xs text-brand-muted uppercase tracking-widest mb-1">
                {wp.balance.available}
              </p>
              <p className="font-display text-display-md text-brand-white">
                {formatPrice('0', 'USDT', locale)}
              </p>
            </div>
          ) : (
            wallet.balances.map((balance) => {
              const total =
                Number(balance.availableBalance) +
                Number(balance.pendingBalance) +
                Number(balance.escrowBalance);
              const cards = [
                {
                  key: 'available',
                  label: wp.balance.available,
                  value: formatPrice(balance.availableBalance, balance.currency, locale),
                  hint: wp.balance.availableHint,
                  icon: CheckCircle,
                  accent: 'text-brand-success',
                },
                {
                  key: 'pending',
                  label: wp.balance.pending,
                  value: formatPrice(balance.pendingBalance, balance.currency, locale),
                  hint: wp.balance.pendingHint,
                  icon: Clock,
                  accent: 'text-accent',
                },
                {
                  key: 'escrow',
                  label: wp.balance.escrow,
                  value: formatPrice(balance.escrowBalance, balance.currency, locale),
                  icon: ShieldCheck,
                  accent: 'text-brand-warning',
                },
                {
                  key: 'total',
                  label: wp.balance.total,
                  value: formatPrice(String(total), balance.currency, locale),
                  icon: Wallet,
                  accent: 'text-accent',
                },
              ];
              return (
                <div key={balance.currency} className="contents">
                  {cards.map((card) => {
                    const Icon = card.icon;
                    return (
                      <div
                        key={card.key}
                        className="p-5 rounded-2xl bg-surface-card border border-surface-border"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-xs font-semibold uppercase tracking-widest text-brand-muted">
                            {card.label}
                          </span>
                          <div className={`flex size-8 items-center justify-center rounded-lg bg-surface-base border border-surface-border ${card.accent}`}>
                            <Icon size={15} />
                          </div>
                        </div>
                        <p className="font-display text-display-sm text-brand-white truncate">
                          {card.value}
                        </p>
                        {card.hint && (
                          <p className={`mt-1 flex items-center gap-1 text-[11px] ${card.accent === 'text-brand-success' ? 'text-brand-success' : 'text-brand-muted'}`}>
                            <Icon size={11} />
                            {card.hint}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      ) : null}

      <div id="deposit" className="p-6 rounded-2xl bg-surface-card border border-surface-border scroll-mt-24">
        <div className="flex items-center gap-2 mb-4">
          <ArrowDownToLine size={20} className="text-brand-success" />
          <h2 className="font-display text-display-md text-brand-white">{wp.deposit.heading}</h2>
        </div>
        <p className="text-sm text-brand-muted mb-6">{wp.deposit.description}</p>

        {depositError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger mb-4"
            role="alert"
          >
            <AlertCircle size={16} />
            {depositError}
          </div>
        )}

        {!depositResult ? (
          <form onSubmit={handleDeposit} className="space-y-4">
            <div>
              <label htmlFor="deposit-amount" className="block text-sm text-brand-white mb-1.5">
                {wp.deposit.amountLabel}
              </label>
              <input
                id="deposit-amount"
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                inputMode="decimal"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                placeholder={wp.deposit.amountPlaceholder}
                required
                className="w-full h-11 rounded-lg bg-surface-base border border-surface-border px-3 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={depositLoading || !depositAmount}
              className="btn-primary h-11 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {depositLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {wp.deposit.submitting}
                </>
              ) : (
                wp.deposit.submit
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-base border border-surface-border px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-xs text-brand-muted uppercase tracking-widest">
                  {wp.deposit.invoiceId}
                </p>
                <code className="block text-sm text-brand-white truncate" title={depositResult.invoiceId}>
                  {depositResult.invoiceId}
                </code>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm text-accent font-medium">
                  {format(depositResult.amount, depositResult.currency)}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy('invoice', depositResult.invoiceId)}
                  aria-label={wp.deposit.copyInvoiceAria}
                  aria-live="polite"
                  className="flex items-center gap-1.5 rounded-md border border-surface-border px-2.5 py-1.5 text-xs font-medium text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
                >
                  <Copy size={13} />
                  {copiedField === 'invoice' ? wp.deposit.copied : wp.deposit.copyLabel}
                </button>
              </div>
            </div>

            <div className="rounded-lg bg-surface-base border border-surface-border px-3 py-2.5">
              <p className="text-xs text-brand-muted uppercase tracking-widest mb-0.5">
                {wp.deposit.txId}
              </p>
              <code className="block text-sm text-brand-white truncate" title={depositResult.txId}>{depositResult.txId}</code>
            </div>

            <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-base border border-surface-border px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-xs text-brand-muted uppercase tracking-widest">
                  {wp.deposit.checkoutUrl}
                </p>
                <code className="block text-sm text-brand-white truncate" title={depositResult.checkoutUrl}>
                  {depositResult.checkoutUrl}
                </code>
              </div>
              <button
                type="button"
                onClick={() => handleCopy('url', depositResult.checkoutUrl)}
                aria-label={wp.deposit.copyUrlAria}
                aria-live="polite"
                className="flex shrink-0 items-center gap-1.5 rounded-md border border-surface-border px-2.5 py-1.5 text-xs font-medium text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
              >
                <Copy size={13} />
                {copiedField === 'url' ? wp.deposit.copied : wp.deposit.copyLabel}
              </button>
            </div>

            {depositStatus === 'COMPLETED' ? (
              <>
                <div
                  className="flex items-center gap-2 rounded-lg border border-brand-success/30 bg-brand-success/10 px-3 py-2.5 text-sm text-brand-success"
                  role="status"
                >
                  <CheckCircle size={16} />
                  <span>{wp.deposit.completed}</span>
                </div>
                <button
                  type="button"
                  onClick={handleNewDeposit}
                  className="btn-secondary h-11 w-full justify-center"
                >
                  {wp.deposit.newInvoice}
                </button>
              </>
            ) : depositStatus === 'EXPIRED' || depositStatus === 'FAILED' ? (
              <>
                <div
                  className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
                  role="alert"
                >
                  <XCircle size={16} />
                  <span>
                    {depositStatus === 'EXPIRED' ? wp.deposit.expiredAction : wp.deposit.failedAction}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleNewDeposit}
                  className="btn-primary h-11 w-full justify-center"
                >
                  {wp.deposit.newInvoice}
                </button>
              </>
            ) : (
              <>
                <a
                  href={depositResult.checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary h-11 w-full justify-center"
                >
                  <ExternalLink size={16} />
                  {wp.deposit.checkoutButton}
                </a>
                <p className="text-xs text-brand-muted">{wp.deposit.checkoutHint}</p>

                <div className="flex items-center gap-2 text-sm text-brand-muted" aria-live="polite">
                  <Clock size={14} className="text-accent" />
                  {wp.deposit.polling}
                  {depositStatus ? (
                    <span className="text-accent ms-1">({depositStatusLabel(depositStatus)})</span>
                  ) : null}
                </div>

                {depositPollError && (
                  <div
                    className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2 text-xs text-brand-danger"
                    role="alert"
                  >
                    <AlertCircle size={13} />
                    {wp.deposit.pollError}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="p-6 rounded-2xl bg-surface-card border border-surface-border">
        <div className="flex items-center gap-2 mb-4">
          <ArrowUpFromLine size={20} className="text-brand-danger" />
          <h2 className="font-display text-display-md text-brand-white">{wp.withdraw.heading}</h2>
        </div>
        <p className="text-sm text-brand-muted mb-6">{wp.withdraw.description}</p>

        {withdrawError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger mb-4"
            role="alert"
          >
            <AlertCircle size={16} />
            {withdrawError}
          </div>
        )}

        {withdrawResult ? (
          <div className="space-y-4">
            <div
              className="flex items-center gap-2 rounded-lg border border-brand-success/30 bg-brand-success/10 px-3 py-2.5 text-sm text-brand-success"
              role="status"
            >
              <CheckCircle size={16} />
              {wp.withdraw.success}
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-base border border-surface-border px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-xs text-brand-muted uppercase tracking-widest">
                  {wp.withdraw.status}
                </p>
                <p className="text-sm text-brand-white truncate">{withdrawResult.status}</p>
              </div>
              <span className="text-sm text-accent font-medium shrink-0">
                {formatPrice(withdrawResult.amount, withdrawResult.currency, locale)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setWithdrawResult(null)}
              className="btn-secondary h-9 text-sm"
            >
              {wp.withdraw.another}
            </button>
          </div>
        ) : wallet && wallet.balances.length > 0 ? (
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div>
              <label htmlFor="withdraw-currency" className="block text-sm text-brand-white mb-1.5">
                {wp.withdraw.currencyLabel}
              </label>
              <select
                id="withdraw-currency"
                value={withdrawCurrency}
                onChange={(e) => setWithdrawCurrency(e.target.value)}
                className="w-full h-11 rounded-lg bg-surface-base border border-surface-border px-3 text-sm text-brand-white focus:outline-none focus:border-accent/30 transition-colors"
              >
                {wallet.balances.map((b) => (
                  <option key={b.currency} value={b.currency}>
                    {b.currency}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="withdraw-amount" className="block text-sm text-brand-white mb-1.5">
                {wp.withdraw.amountLabel}
              </label>
              <input
                id="withdraw-amount"
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                inputMode="decimal"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder={wp.withdraw.amountPlaceholder}
                required
                className="w-full h-11 rounded-lg bg-surface-base border border-surface-border px-3 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="withdraw-destination" className="block text-sm text-brand-white mb-1.5">
                {wp.withdraw.destinationLabel}
              </label>
              <input
                id="withdraw-destination"
                type="text"
                value={withdrawDestination}
                onChange={(e) => setWithdrawDestination(e.target.value)}
                placeholder={wp.withdraw.destinationPlaceholder}
                className="w-full h-11 rounded-lg bg-surface-base border border-surface-border px-3 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none focus:border-accent/30 transition-colors"
              />
            </div>
            <p className="text-xs text-brand-muted">
              {wp.withdraw.availableLabel}:{' '}
              <span className="text-brand-white">
                {wallet.balances.find((b) => b.currency === withdrawCurrency)
                  ? formatPrice(
                      wallet.balances.find((b) => b.currency === withdrawCurrency)!.availableBalance,
                      withdrawCurrency,
                      locale,
                    )
                  : formatPrice('0', withdrawCurrency || 'USDT', locale)}
              </span>
            </p>
            <button
              type="submit"
              disabled={withdrawLoading || !withdrawAmount}
              className="btn-primary h-11 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {withdrawLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {wp.withdraw.submitting}
                </>
              ) : (
                wp.withdraw.submit
              )}
            </button>
          </form>
        ) : (
          <p className="text-sm text-brand-muted">{wp.withdraw.empty}</p>
        )}
      </div>

      <div className="p-6 rounded-2xl bg-surface-card border border-surface-border">
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2">
            <History size={20} className="text-brand-muted" />
            <h2 className="font-display text-display-md text-brand-white">{wp.history.heading}</h2>
          </div>
          {transactions.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setExportOpen((o) => !o)}
                className="flex items-center gap-2 rounded-lg border border-surface-border px-3 py-2 text-xs font-medium text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
              >
                <Download size={14} />
                {wp.history.export}
              </button>
              {exportOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                  <div className="absolute end-0 mt-1 z-20 w-44 rounded-xl bg-surface-card border border-surface-border shadow-card overflow-hidden">
                    <button
                      type="button"
                      onClick={() => { exportCSV(transactions); setExportOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-brand-muted hover:bg-surface-hover hover:text-brand-white transition-colors"
                    >
                      <FileSpreadsheet size={14} />
                      {wp.history.exportCSV}
                    </button>
                    <button
                      type="button"
                      onClick={() => { printAllTransactions(); setExportOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-brand-muted hover:bg-surface-hover hover:text-brand-white transition-colors"
                    >
                      <Printer size={14} />
                      {wp.history.exportPDF}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {transactionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-accent" />
          </div>
        ) : transactionsError ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
            role="alert"
          >
            <AlertCircle size={16} />
            {transactionsError}
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-sm text-brand-muted text-center py-8">{wp.history.empty}</p>
        ) : (() => {
          const totalPages = Math.ceil(transactions.length / txPageSize);
          const pageTx = transactions.slice(txPage * txPageSize, (txPage + 1) * txPageSize);

          return (
          <>
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-brand-muted">{wp.history.show}</span>
                {([10, 50, 100] as const).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { setTxPageSize(n); setTxPage(0); }}
                    className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                      txPageSize === n
                        ? 'bg-accent/10 text-accent border border-accent/20'
                        : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={txPage === 0}
                  onClick={() => setTxPage((p) => p - 1)}
                  className="px-2 py-1 rounded text-xs text-brand-muted hover:bg-surface-hover hover:text-brand-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {wp.history.prev}
                </button>
                <span className="px-2 text-xs text-brand-muted">
                  {txPage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={txPage >= totalPages - 1}
                  onClick={() => setTxPage((p) => p + 1)}
                  className="px-2 py-1 rounded text-xs text-brand-muted hover:bg-surface-hover hover:text-brand-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {wp.history.next}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-border">
                  <th className="text-start py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest w-28">
                    {wp.history.id}
                  </th>
                  <th className="text-start py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest">
                    {wp.history.type}
                  </th>
                  <th className="text-start py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest">
                    {wp.history.amount}
                  </th>
                  <th className="text-start py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest">
                    {wp.history.status}
                  </th>
                  <th className="text-end py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest">
                    {wp.history.date}
                  </th>
                  <th className="text-end py-3 px-2 text-xs font-semibold text-brand-muted uppercase tracking-widest w-16">
                    <span className="sr-only">{wp.history.actions}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageTx.map((tx) => (
                  <tr key={tx.id} className="border-b border-surface-border">
                    <td className="py-3 px-2 text-brand-muted font-mono text-xs" title={tx.id}>
                      {tx.id.slice(0, 8)}&hellip;
                    </td>
                    <td className="py-3 px-2 text-brand-white">{wp.history.types[tx.type]}</td>
                    <td
                      className={`py-3 px-2 ${
                        tx.type === 'DEPOSIT'
                          ? 'text-brand-success'
                          : tx.type === 'WITHDRAWAL'
                            ? 'text-brand-danger'
                            : 'text-accent'
                      }`}
                    >
                      {tx.type === 'DEPOSIT' ? '+' : '-'}
                      {format(tx.amount, tx.currency)}
                    </td>
                    <td className="py-3 px-2">{statusBadge(tx.status)}</td>
                    <td className="py-3 px-2 text-brand-muted text-end">{formatDate(tx.createdAt)}</td>
                    <td className="py-3 px-2 text-end">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => exportCSV([tx])}
                          aria-label={wp.history.csv}
                          className="p-1 rounded text-brand-muted hover:text-brand-white hover:bg-surface-hover transition-colors"
                        >
                          <FileSpreadsheet size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => printTransaction(tx)}
                          aria-label={wp.history.pdf}
                          className="p-1 rounded text-brand-muted hover:text-brand-white hover:bg-surface-hover transition-colors"
                        >
                          <Printer size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          );
        })()}
      </div>
    </div>
  );
}
